/**
 * Inline guide quiz ("Which rain suit fits you?") for buying-guide articles.
 *
 * Markup and config come from snippets/guide-quiz.liquid and a per-guide
 * config snippet (JSON in [data-wr-quiz-config]). This file holds no guide
 * content, so a new guide only needs a new config.
 *
 * Flow: one question per screen -> result shown immediately (never gated)
 * -> optional "Email me my results" form, rendered by Liquid only when a
 * Klaviyo list ID is configured.
 *
 * GA4 events (gtag, same pattern as article-guide-cta.js):
 *   guide_quiz_start        first answer picked in a run
 *   guide_quiz_answer       each question submitted
 *   guide_quiz_result       result shown (result_pick, result_rule)
 *   guide_quiz_email_submit Klaviyo accepted the signup
 * No email address or other PII is ever sent to GA4.
 */
(function () {
  'use strict';

  if (window.__wrGuideQuizInit) return;
  window.__wrGuideQuizInit = true;

  var MARKER_PARAM = 'wr_lp_test';
  var KLAVIYO_REVISION = '2024-10-15';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
      }
    } catch {
      /* Analytics must never break the quiz. */
    }
  }

  /** Small element builder: el('p', 'class-name', 'text'). */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /** Keep the paid-test marker on store links, as article-guide-cta.js does for static links. */
  function withMarker(href) {
    if (!href || href.charAt(0) === '#') return href;
    try {
      var marker = new window.URLSearchParams(window.location.search).get(MARKER_PARAM);
      if (!marker) return href;
      var url = new window.URL(href, window.location.origin);
      url.searchParams.set(MARKER_PARAM, marker);
      return url.pathname + url.search + url.hash;
    } catch {
      return href;
    }
  }

  function GuideQuiz(root, config) {
    this.root = root;
    this.config = config;
    this.form = root.querySelector('[data-wr-quiz-form]');
    this.resultBox = root.querySelector('[data-wr-quiz-result]');
    this.emailForm = root.querySelector('[data-wr-quiz-email]');
    this.live = root.querySelector('[data-wr-quiz-live]');
    this.baseId = root.id || 'guide-quiz';
    this.reset();
    this.bind();
    this.renderStep(false);
  }

  GuideQuiz.prototype.reset = function () {
    this.step = 0;
    this.answers = {};
    this.started = false;
  };

  GuideQuiz.prototype.params = function (extra) {
    return Object.assign({ quiz_id: this.config.quizId }, extra || {});
  };

  /* ---------------------------------------------------------- questions */

  GuideQuiz.prototype.renderStep = function (moveFocus) {
    var self = this;
    var questions = this.config.questions;
    var question = questions[this.step];
    var total = questions.length;
    var headingId = this.baseId + '-' + question.id + '-heading';
    var errorId = this.baseId + '-' + question.id + '-error';

    var fieldset = el('fieldset', 'wr-quiz__step');
    fieldset.setAttribute('aria-describedby', errorId);

    var legend = el('legend', 'wr-quiz__legend');
    var progress = el('span', 'wr-quiz__progress', 'Question ' + (this.step + 1) + ' of ' + total);
    var bar = el('span', 'wr-quiz__bar');
    bar.setAttribute('aria-hidden', 'true');
    bar.style.setProperty('--wr-quiz-progress', ((this.step + 1) / total) * 100 + '%');
    var heading = el('h3', 'wr-quiz__question', question.question);
    heading.id = headingId;
    heading.tabIndex = -1;
    legend.appendChild(progress);
    legend.appendChild(bar);
    legend.appendChild(heading);
    fieldset.appendChild(legend);

    var options = el('div', 'wr-quiz__options');
    question.options.forEach(function (option) {
      var label = el('label', 'wr-quiz__option');
      var input = document.createElement('input');
      input.type = 'radio';
      input.className = 'wr-quiz__radio';
      input.name = self.baseId + '-' + question.id;
      input.value = option.value;
      input.checked = self.answers[question.id] === option.value;
      label.appendChild(input);
      label.appendChild(el('span', 'wr-quiz__option-label', option.label));
      options.appendChild(label);
    });
    fieldset.appendChild(options);

    var error = el('p', 'wr-quiz__error');
    error.id = errorId;
    error.setAttribute('data-wr-quiz-error', '');
    fieldset.appendChild(error);

    var nav = el('div', 'wr-quiz__nav');
    if (this.step > 0) {
      var back = el('button', 'wr-quiz__button wr-quiz__button--ghost', 'Back');
      back.type = 'button';
      back.setAttribute('data-wr-quiz-back', '');
      nav.appendChild(back);
    }
    var next = el(
      'button',
      'wr-quiz__button wr-quiz__button--primary',
      this.step === total - 1 ? 'See my result' : 'Next'
    );
    next.type = 'submit';
    nav.appendChild(next);
    fieldset.appendChild(nav);

    this.form.replaceChildren(fieldset);
    this.form.hidden = false;
    if (moveFocus) heading.focus();
  };

  GuideQuiz.prototype.submitStep = function () {
    var question = this.config.questions[this.step];
    var checked = this.form.querySelector('input[type="radio"]:checked');
    var error = this.form.querySelector('[data-wr-quiz-error]');

    if (!checked) {
      error.textContent = 'Pick one answer to continue.';
      var first = this.form.querySelector('input[type="radio"]');
      if (first) first.focus();
      return;
    }

    this.answers[question.id] = checked.value;
    track(
      'guide_quiz_answer',
      this.params({ question_id: question.id, question_number: this.step + 1, answer_value: checked.value })
    );

    if (this.step < this.config.questions.length - 1) {
      this.step += 1;
      this.renderStep(true);
    } else {
      this.showResult();
    }
  };

  /* -------------------------------------------------------------- rules */

  GuideQuiz.prototype.match = function () {
    var answers = this.answers;
    var rules = this.config.rules || [];

    for (var i = 0; i < rules.length; i++) {
      var when = rules[i].when || {};
      var matches = Object.keys(when).every(function (questionId) {
        return when[questionId].indexOf(answers[questionId]) !== -1;
      });
      if (matches) return rules[i];
    }

    return this.config.fallback;
  };

  /* ------------------------------------------------------------- result */

  GuideQuiz.prototype.link = function (href, label, className, isStoreCta, placement) {
    var link = el('a', className);
    link.href = withMarker(href);
    link.appendChild(document.createTextNode(label + ' '));
    var arrow = el('span', '', href.charAt(0) === '#' ? '↓' : '→');
    arrow.setAttribute('aria-hidden', 'true');
    link.appendChild(arrow);
    if (isStoreCta) {
      // Picked up by article-guide-cta.js: sends guide_cta_click and holds
      // same-tab navigation until the hit is delivered.
      link.setAttribute('data-wr-cta-button', '');
      link.setAttribute('data-wr-cta-placement', placement);
    }
    return link;
  };

  GuideQuiz.prototype.showResult = function () {
    var rule = this.match();
    var pickId = rule.result;
    var pick = this.config.results[pickId];
    var headingId = this.baseId + '-result-heading';

    var card = el('div', 'wr-quiz__card');
    card.appendChild(el('p', 'wr-quiz__eyebrow', 'Your pick'));

    var heading = el('h3', 'wr-quiz__result-name', pick.name);
    heading.id = headingId;
    heading.tabIndex = -1;
    card.appendChild(heading);

    if (pick.badge) card.appendChild(el('p', 'wr-quiz__badge', pick.badge));
    card.appendChild(el('p', 'wr-quiz__price', pick.price));
    card.appendChild(el('p', 'wr-quiz__reason', pick.reason));
    if (pick.note) card.appendChild(el('p', 'wr-quiz__note', pick.note));

    var actions = el('div', 'wr-quiz__actions');
    actions.appendChild(
      this.link(pick.href, pick.linkLabel, 'wr-quiz__button wr-quiz__button--primary', !!pick.ours, 'quiz-result')
    );
    actions.appendChild(this.link(this.config.allPicksHref, this.config.allPicksLabel, 'wr-quiz__text-link', false));
    card.appendChild(actions);

    var altPick = rule.alt && this.config.altPick && this.config.results[this.config.altPick];
    if (altPick && pick.altHeading) {
      var alt = el('div', 'wr-quiz__alt');
      alt.appendChild(el('p', 'wr-quiz__alt-heading', pick.altHeading));
      alt.appendChild(el('p', 'wr-quiz__alt-text', pick.altText));
      alt.appendChild(
        this.link(altPick.href, altPick.linkLabel, 'wr-quiz__text-link wr-quiz__text-link--strong', !!altPick.ours, 'quiz-alt')
      );
      card.appendChild(alt);
    }

    var restart = el('button', 'wr-quiz__button wr-quiz__button--ghost wr-quiz__restart', 'Start over');
    restart.type = 'button';
    restart.setAttribute('data-wr-quiz-restart', '');
    card.appendChild(restart);

    this.resultBox.replaceChildren(card);
    this.form.hidden = true;
    this.form.replaceChildren();
    this.resultBox.hidden = false;
    this.lastResult = { id: pickId, name: pick.name, href: pick.href };
    this.showEmail();

    if (this.live) this.live.textContent = 'Your pick: ' + pick.name + '.';
    heading.focus();

    track('guide_quiz_result', this.params({ result_pick: pickId, result_rule: rule.id }));
  };

  GuideQuiz.prototype.restart = function () {
    this.reset();
    this.resultBox.hidden = true;
    this.resultBox.replaceChildren();
    if (this.emailForm) this.emailForm.hidden = true;
    if (this.live) this.live.textContent = '';
    this.renderStep(true);
  };

  /* -------------------------------------------------------------- email */

  GuideQuiz.prototype.showEmail = function () {
    if (!this.emailForm) return;
    var status = this.emailForm.querySelector('[data-wr-quiz-email-status]');
    var button = this.emailForm.querySelector('button[type="submit"]');
    this.emailForm.removeAttribute('data-state');
    status.textContent = '';
    button.disabled = false;
    this.emailForm.hidden = false;
  };

  GuideQuiz.prototype.submitEmail = function () {
    var self = this;
    var form = this.emailForm;
    var input = form.querySelector('input[type="email"]');
    var status = form.querySelector('[data-wr-quiz-email-status]');
    var button = form.querySelector('button[type="submit"]');
    var email = (input.value || '').trim();

    if (form.getAttribute('data-state') === 'sending' || form.getAttribute('data-state') === 'done') return;

    if (!EMAIL_PATTERN.test(email)) {
      input.setAttribute('aria-invalid', 'true');
      form.setAttribute('data-state', 'error');
      status.textContent = 'Enter a valid email address.';
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    form.setAttribute('data-state', 'sending');
    status.textContent = 'Sending…';
    button.disabled = true;

    var result = this.lastResult || {};
    var resultUrl = window.location.origin + window.location.pathname + (result.href || '');
    if (result.href && result.href.charAt(0) !== '#') resultUrl = window.location.origin + result.href;

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Guide Quiz: ' + this.config.quizId,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties: {
                  guide_quiz_id: this.config.quizId,
                  guide_quiz_result: result.name || '',
                  guide_quiz_result_id: result.id || '',
                  guide_quiz_result_url: resultUrl
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: form.getAttribute('data-list-id') } }
        }
      }
    };

    window
      .fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' +
          encodeURIComponent(form.getAttribute('data-company-id')),
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(body)
        }
      )
      .then(function (response) {
        if (!response.ok) throw new Error('Klaviyo ' + response.status);
        form.setAttribute('data-state', 'done');
        status.textContent = 'Thanks — check your inbox for your results.';
        track('guide_quiz_email_submit', self.params({ result_pick: result.id || '' }));
      })
      .catch(function () {
        form.setAttribute('data-state', 'error');
        status.textContent = 'Something went wrong. Please try again.';
        button.disabled = false;
      });
  };

  /* ------------------------------------------------------------- events */

  GuideQuiz.prototype.bind = function () {
    var self = this;

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitStep();
    });

    this.form.addEventListener('change', function (event) {
      if (!event.target.matches('input[type="radio"]')) return;
      var error = self.form.querySelector('[data-wr-quiz-error]');
      if (error) error.textContent = '';
      if (!self.started) {
        self.started = true;
        track('guide_quiz_start', self.params());
      }
    });

    this.root.addEventListener('click', function (event) {
      if (event.target.closest('[data-wr-quiz-back]')) {
        self.step = Math.max(0, self.step - 1);
        self.renderStep(true);
      } else if (event.target.closest('[data-wr-quiz-restart]')) {
        self.restart();
      }
    });

    if (this.emailForm) {
      this.emailForm.addEventListener('submit', function (event) {
        event.preventDefault();
        self.submitEmail();
      });
    }
  };

  /* --------------------------------------------------------------- init */

  function init() {
    var roots = document.querySelectorAll('[data-wr-quiz]');

    Array.prototype.forEach.call(roots, function (root) {
      var source = root.querySelector('[data-wr-quiz-config]');
      if (!source) return;
      try {
        var config = JSON.parse(source.textContent);
        if (!config.questions || !config.questions.length || !config.results) return;
        new GuideQuiz(root, config);
      } catch {
        /* Bad config: leave the server-rendered fallback link in place. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
